import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { appointmentResponseSchema } from "../../../services/prePlanningService.types";
import { http } from "../../../utils/httpCommon";
import { useApiUrls } from "../../useApiUrl";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";

const APPOINTMENTS_SEARCH_TERM_KEY = "apptSearchTerm";

const appointmentsQueryKey = (
  sicId: number,
  planDate: string,
  apptSearchTerm: string | null = ""
) => ["pre-planning", sicId, "appointments", planDate, apptSearchTerm];

export function useGetAppointmentBucketData(
  sicId: number,
  planDate: string,
  apptSearchTerm = ""
) {
  const hookUrls = useApiUrls();
  const { getAppointmentBucket } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: true,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: appointmentsQueryKey(sicId, planDate, apptSearchTerm),
    queryFn: async () => {
      const url = getAppointmentBucket(sicId, planDate, apptSearchTerm);
      const { data } = await http.get(url);
      return isSafeParseEnabled
        ? safeParsePartial(
            appointmentResponseSchema,
            data,
            "useGetAppointmentBucketData"
          )
        : appointmentResponseSchema.parse(data);
    },
  });
}

export function useInvalidateQueryAppointmentBucketData() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const invalidateQueryAppointmentBucketData = (
    sicId: number,
    planDate: string
  ) => {
    queryClient.invalidateQueries({
      queryKey: appointmentsQueryKey(
        sicId,
        planDate,
        searchParams.get(APPOINTMENTS_SEARCH_TERM_KEY) ?? ""
      ),
    });
  };

  return { invalidateQueryAppointmentBucketData };
}
